// src/components/Profile.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  updateUserPreferences
} from '../services/api';
import supabase from '../supabase';
import './Profile.css';

// Use environment-based API URL
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const Profile = () => {
  const { user, role, getToken, logout } = useAuth();
  const userId = user?.id;

  // Change password states
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Email preferences states
  const [emailUpdates, setEmailUpdates] = useState(user?.wants_updates || false);
  const [updatingPreferences, setUpdatingPreferences] = useState(false);

  // User stats states
  const [userStats, setUserStats] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Initialize email updates state when user is loaded
  useEffect(() => {
    if (user) {
      setEmailUpdates(user.wants_updates || false);
    }
    
    // Fetch user stats if super_user or admin
    if (role === 'super_user' || role === 'admin') {
      fetchUserStats();
    }
  }, [user, role]);

  // Fetch user stats
  const fetchUserStats = async () => {
    if (!userId) return;
    
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/users/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserStats(data);
      } else {
        console.error('Error fetching stats:', await response.text());
      }
    } catch (err) {
      console.error('Error fetching user stats:', err);
    }
  };

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    try {
      const { error } = await supabase.auth.updateUser({ 
        password: newPassword,
        currentPassword: currentPassword 
      });
      
      if (error) throw error;
      
      setPasswordSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  // Email preferences toggle handler
  const handleToggleEmailUpdates = async () => {
    if (!userId) return;
    
    setUpdatingPreferences(true);
    try {
      const token = getToken();
      await updateUserPreferences(token, !emailUpdates);
      setEmailUpdates(!emailUpdates);
    } catch (err) {
      console.error('Error updating email preferences:', err);
    } finally {
      setUpdatingPreferences(false);
    }
  };

  // Delete user account
  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account?')) return;
    try {
      const token = getToken();
      if (!token) {
        alert('No token found.');
        return;
      }
      const response = await fetch(`${API_BASE}/api/users/me`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(await response.text());
      alert('Account deleted. Goodbye!');
      logout();
    } catch (err) {
      console.error('[Profile] Error deleting account:', err);
      alert(`Error: ${err.message}`);
    }
  };

  if (!userId) {
    return <p>Please log in to view your profile.</p>;
  }

  return (
    <div className="container" style={{ textAlign: 'left' }}>
      {user?.first_name ? (
        <h2>Welcome, {user.first_name}!</h2>
      ) : (
        <h2>{user?.email}</h2>
      )}

      {role === 'admin' && (
        <p>
          <Link to="/admin">Go to Admin Panel</Link>
        </p>
      )}
      
      <hr />
      
      {/* SECTION: Communication Preferences */}
      <div style={{ marginTop: '2rem' }}>
        <h2>Communication Preferences</h2>
        
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
          <input
            type="checkbox"
            id="emailUpdates"
            checked={emailUpdates}
            onChange={handleToggleEmailUpdates}
            disabled={updatingPreferences}
          />
          <label htmlFor="emailUpdates" style={{ marginLeft: '0.5rem' }}>
            Receive product updates via email
          </label>
          {updatingPreferences && <span style={{ marginLeft: '0.5rem' }}>Updating...</span>}
        </div>
      </div>

      {/* SECTION: Account Security */}
      <div style={{ marginTop: '2rem' }}>
        <hr />
        <h2>Account Security</h2>
        
        <button 
          onClick={() => setShowPasswordForm(!showPasswordForm)}
          style={{ marginBottom: '1rem' }}
        >
          {showPasswordForm ? 'Cancel' : 'Change Password'}
        </button>
        
        {showPasswordForm && (
          <form onSubmit={handlePasswordChange}>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="currentPassword">Current Password:</label>
              <br />
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="newPassword">New Password:</label>
              <br />
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="confirmPassword">Confirm New Password:</label>
              <br />
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            
            <button type="submit">Update Password</button>
            
            {passwordError && (
              <p style={{ color: 'red', marginTop: '0.5rem' }}>{passwordError}</p>
            )}
            
            {passwordSuccess && (
              <p style={{ color: 'green', marginTop: '0.5rem' }}>{passwordSuccess}</p>
            )}
          </form>
        )}
      </div>

      {/* SECTION: Contribution Stats for super_user and admin */}
      {(role === 'super_user' || role === 'admin') && (
        <div style={{ marginTop: '2rem' }}>
          <hr />
          <h2>Your Contributions</h2>
          {userStats ? (
            <div style={{ marginTop: '0.5rem' }}>
              <p>Pairings created: {userStats.pairingsCreated}</p>
              <p>Pairings voted on: {userStats.pairingsVotedOn}</p>
              <p>Total contributions: {userStats.pairingsCreated + userStats.pairingsVotedOn}</p>
            </div>
          ) : (
            <p>Loading contribution stats...</p>
          )}
        </div>
      )}

      {/* SECTION: Legal & Info */}
      <div style={{ marginTop: '2rem' }}>
        <hr />
        <h2>Legal & Info</h2>
        <p>
          <Link to="/about">About isLike</Link>
        </p>
        <p>
          <Link to="/terms">Terms of Use</Link>
        </p>
        <p>
          <Link to="/privacy">Privacy Policy</Link>
        </p>
      </div>

      <div style={{ marginTop: '2rem' }}>
        <hr />
        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
          <span
            style={{ color: 'red', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleDeleteAccount}
          >
            Delete My Account
          </span>
        </p>
      </div>
    </div>
  );
};

export default Profile;